import { useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { DESKTOP_HEADER_HEIGHT, MOBILE_HEADER_HEIGHT } from "../constants/design";

const MOBILE_MQ = "(max-width: 768px)";

function getHeaderHeight(): number {
  return window.matchMedia(MOBILE_MQ).matches ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT;
}

export const HEADER_THEME_REFRESH = "header-theme-refresh";

function detectHeaderTheme(): "white" | "red" | null {
  const sections = document.querySelectorAll<HTMLElement>("[data-header-theme]");
  const checkY = getHeaderHeight() / 2;

  let matched: "white" | "red" | null = null;
  let matchedHeight = Infinity;

  sections.forEach((el) => {
    const { top, bottom } = el.getBoundingClientRect();
    if (top <= checkY && bottom > checkY) {
      const height = bottom - top;
      if (height < matchedHeight) {
        matchedHeight = height;
        matched = el.dataset.headerTheme as "white" | "red";
      }
    }
  });

  return matched;
}

export function useHeaderTheme(): "white" | "red" {
  const [theme, setTheme] = useState<"white" | "red">("white");
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const update = () => {
      const matched = detectHeaderTheme();
      if (matched !== null) {
        setTheme((prev) => (prev === matched ? prev : matched));
      }
    };

    let rafId = 0;
    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };

    update();
    const raf = requestAnimationFrame(update);
    const afterScrollReset = window.setTimeout(update, 0);

    const isMobile = window.matchMedia(MOBILE_MQ).matches;
    let resizeObserver: ResizeObserver | undefined;

    if (!isMobile) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      document.querySelectorAll<HTMLElement>("[data-header-theme]").forEach((el) => {
        resizeObserver?.observe(el);
      });
      const section04 = document.getElementById("section04");
      if (section04) resizeObserver.observe(section04);
    }

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });
    window.addEventListener(HEADER_THEME_REFRESH, scheduleUpdate);

    return () => {
      cancelAnimationFrame(raf);
      if (rafId) cancelAnimationFrame(rafId);
      window.clearTimeout(afterScrollReset);
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener(HEADER_THEME_REFRESH, scheduleUpdate);
    };
  }, [pathname]);

  return theme;
}
